import { parseCalculatorInspectorModelV1 } from "./calculator-layer-inspector-contract.js";
import { canonicalizeCalculatorFishingSourceV1 } from "./calculator-fishing-source.js";

const MODEL_FIELDS = Object.freeze([
    "schemaVersion",
    "engineVersion",
    "resolvedRevision",
    "modelRevision",
    "modelHash",
    "selection",
    "baseFacts",
    "sourceLayers",
    "catalog",
    "defaultScenario",
    "provenanceCatalog",
    "fishingSourceCatalog",
    "conditionCatalog",
    "branchIdCatalog",
    "rateAlternativeCatalog",
]);
const INTERNED_FIELDS = Object.freeze([
    Object.freeze({ field: "provenance", reference: "provenanceRef", catalog: "provenanceCatalog" }),
    Object.freeze({ field: "fishingSource", reference: "fishingSourceRef", catalog: "fishingSourceCatalog" }),
    Object.freeze({ field: "conditions", reference: "conditionsRef", catalog: "conditionCatalog" }),
    Object.freeze({ field: "branchId", reference: "branchIdRef", catalog: "branchIdCatalog" }),
    Object.freeze({ field: "alternative", reference: "alternativeRef", catalog: "rateAlternativeCatalog" }),
]);
const REQUIRED = "required";
const OPTIONAL = "optional";
const OPERATION_REFERENCES = Object.freeze({
    groupMembership: Object.freeze({ fishingSource: REQUIRED, conditions: REQUIRED }),
    itemPresence: Object.freeze({ fishingSource: REQUIRED, conditions: OPTIONAL }),
    groupPresence: Object.freeze({ fishingSource: REQUIRED, conditions: OPTIONAL }),
    itemRate: Object.freeze({
        fishingSource: REQUIRED,
        conditions: OPTIONAL,
        branchId: REQUIRED,
        alternative: OPTIONAL,
    }),
    itemQuantity: Object.freeze({ fishingSource: REQUIRED }),
    itemQuantityDefault: Object.freeze({}),
    groupRate: Object.freeze({
        fishingSource: REQUIRED,
        conditions: REQUIRED,
        branchId: REQUIRED,
        alternative: OPTIONAL,
    }),
    optionSelectionRule: Object.freeze({}),
    itemPrice: Object.freeze({}),
    tradeCurve: Object.freeze({}),
    itemEffect: Object.freeze({}),
    petOption: Object.freeze({}),
    tradeFact: Object.freeze({}),
    refuteEvidence: Object.freeze({}),
    fishingSourceBiteTime: Object.freeze({ fishingSource: REQUIRED }),
    masteryPrizeRate: Object.freeze({}),
    durabilityReduction: Object.freeze({}),
    itemIdentity: Object.freeze({}),
    itemExperience: Object.freeze({}),
});
const FACT_FIELDS = Object.freeze(["kind", "operationId", "factId", "provenanceRef"]);
const PROVENANCE_REQUIRED_FIELDS = Object.freeze([
    "sourceId",
    "sourceName",
    "sourceRevision",
    "sourceTable",
    "sourceSheet",
    "sourceRow",
    "contentHash",
    "importedAtUtc",
    "observedAtUtc",
    "observationScope",
    "observationCount",
]);
const PROVENANCE_OPTIONAL_FIELDS = Object.freeze([
    "comment",
    "confidence",
    "author",
    "createdAtUtc",
    "claimDate",
]);
const U32_MAX = 4_294_967_295;
const OPERATION_SHAPES = Object.freeze({
    groupMembership: Object.freeze({
        required: [...FACT_FIELDS, "fishingSourceRef", "itemId", "groupId", "membership", "conditionsRef"],
    }),
    itemPresence: Object.freeze({
        required: [...FACT_FIELDS, "fishingSourceRef", "itemId", "presence"],
        optional: ["conditionsRef"],
    }),
    groupPresence: Object.freeze({
        required: [...FACT_FIELDS, "fishingSourceRef", "groupId", "role", "presence"],
        optional: ["conditionsRef"],
    }),
    itemRate: Object.freeze({
        required: [...FACT_FIELDS, "fishingSourceRef", "itemId", "groupId", "rate", "branchIdRef"],
        optional: [
            "conditionsRef",
            "alternativeRef",
        ],
    }),
    itemQuantity: Object.freeze({
        required: [...FACT_FIELDS, "fishingSourceRef", "itemId", "groupId", "quantity"],
    }),
    itemQuantityDefault: Object.freeze({
        required: [...FACT_FIELDS, "itemId", "quantity"],
    }),
    groupRate: Object.freeze({
        required: [...FACT_FIELDS, "fishingSourceRef", "groupId", "rate", "conditionsRef", "branchIdRef"],
        optional: ["alternativeRef"],
    }),
    optionSelectionRule: Object.freeze({
        required: [...FACT_FIELDS, "optionId", "selectionGroupId", "priority"],
    }),
    itemPrice: Object.freeze({ required: [...FACT_FIELDS, "itemId", "price"] }),
    tradeCurve: Object.freeze({ required: [...FACT_FIELDS, "itemId", "pricePercent"] }),
    itemEffect: Object.freeze({ required: [...FACT_FIELDS, "subject", "effect", "value"] }),
    petOption: Object.freeze({
        required: [
            ...FACT_FIELDS,
            "petId",
            "tier",
            "optionId",
            "optionKind",
            "learnChance",
            "effects",
        ],
    }),
    tradeFact: Object.freeze({ required: [...FACT_FIELDS, "fact"] }),
    refuteEvidence: Object.freeze({
        required: ["kind", "operationId", "provenanceRef", "targetFactId", "reasonCode"],
    }),
    fishingSourceBiteTime: Object.freeze({
        required: [...FACT_FIELDS, "fishingSourceRef", "minimum", "maximum"],
    }),
    masteryPrizeRate: Object.freeze({ required: [...FACT_FIELDS, "mastery", "rate"] }),
    durabilityReduction: Object.freeze({
        required: [...FACT_FIELDS, "lifeskillLevel", "rate"],
    }),
    itemIdentity: Object.freeze({
        required: [...FACT_FIELDS, "itemId", "catalogOptionId", "grade", "isFish"],
    }),
    itemExperience: Object.freeze({
        required: [
            ...FACT_FIELDS,
            "itemId",
            "fishingExperience",
            "lifeExperience",
            "totemExperience",
        ],
    }),
});

function isRecord(value) {
    return Boolean(value)
        && typeof value === "object"
        && !Array.isArray(value)
        && Object.prototype.toString.call(value) === "[object Object]";
}

function fail(path, message) {
    throw new CalculatorCompactModelContractError(path, message);
}

function requireRecord(value, path) {
    if (!isRecord(value)) {
        fail(path, "expected an object");
    }
    return value;
}

function requireExactFields(value, fields, path) {
    const object = requireRecord(value, path);
    const expected = [...fields].sort();
    const actual = Object.keys(object).sort();
    if (
        actual.length !== expected.length
        || actual.some((field, index) => field !== expected[index])
    ) {
        fail(path, `fields must be exactly: ${expected.join(", ")}`);
    }
    return object;
}

function requireFields(value, required, optional, path) {
    const object = requireRecord(value, path);
    const allowed = new Set([...required, ...optional]);
    const unknown = Object.keys(object).find((field) => !allowed.has(field));
    if (unknown) {
        fail(`${path}.${unknown}`, "unknown field");
    }
    const missing = required.find(
        (field) => !Object.prototype.hasOwnProperty.call(object, field),
    );
    if (missing) {
        fail(`${path}.${missing}`, "missing field");
    }
    return object;
}

function requireArray(value, path) {
    if (!Array.isArray(value)) {
        fail(path, "expected an array");
    }
    return value;
}

function requireString(value, path) {
    if (typeof value !== "string" || !value || value.trim() !== value) {
        fail(path, "expected a non-empty string without surrounding whitespace");
    }
    return value;
}

function requireSourceExpression(value, path) {
    if (typeof value !== "string" || !value.trim()) {
        fail(path, "expected a non-blank source expression");
    }
    return value;
}

function requireNullableString(value, path) {
    return value === null ? null : requireString(value, path);
}

function requireInteger(value, path, minimum = 0, maximum = Number.MAX_SAFE_INTEGER) {
    if (!Number.isSafeInteger(value) || value < minimum || value > maximum) {
        fail(path, `expected a safe integer from ${minimum} through ${maximum}`);
    }
    return value;
}

function requireFiniteNonNegative(value, path) {
    if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
        fail(path, "expected a finite non-negative number");
    }
    return value;
}

function validateProvenance(value, path) {
    const provenance = requireFields(
        value,
        PROVENANCE_REQUIRED_FIELDS,
        PROVENANCE_OPTIONAL_FIELDS,
        path,
    );
    requireString(provenance.sourceId, `${path}.sourceId`);
}

function validateCondition(value, path) {
    const candidate = requireRecord(value, path);
    if (candidate.kind !== "numeric") {
        fail(`${path}.kind`, "expected numeric");
    }
    const condition = requireExactFields(
        candidate,
        ["kind", "subject", "comparison", "value"],
        path,
    );
    requireString(condition.subject, `${path}.subject`);
    requireString(condition.comparison, `${path}.comparison`);
    requireFiniteNonNegative(condition.value, `${path}.value`);
}

function validateRateAlternative(value, path) {
    const alternative = requireExactFields(
        value,
        ["familyId", "alternativeId", "priority", "applicability"],
        path,
    );
    requireString(alternative.familyId, `${path}.familyId`);
    requireString(alternative.alternativeId, `${path}.alternativeId`);
    requireInteger(alternative.priority, `${path}.priority`, 0, U32_MAX);
    const applicabilityPath = `${path}.applicability`;
    const applicability = requireRecord(alternative.applicability, applicabilityPath);
    if (applicability.kind === "fallback") {
        requireExactFields(applicability, ["kind"], applicabilityPath);
        return;
    }
    if (applicability.kind === "typedConditions") {
        const typed = requireExactFields(
            applicability,
            ["kind", "conditions"],
            applicabilityPath,
        );
        requireArray(typed.conditions, `${applicabilityPath}.conditions`)
            .forEach((condition, index) => validateCondition(
                condition,
                `${applicabilityPath}.conditions[${index}]`,
            ));
        if (typed.conditions.length === 0) {
            fail(`${applicabilityPath}.conditions`, "expected at least one condition");
        }
        return;
    }
    if (applicability.kind === "sourceExpression") {
        const source = requireExactFields(
            applicability,
            ["kind", "expression"],
            applicabilityPath,
        );
        requireSourceExpression(source.expression, `${applicabilityPath}.expression`);
        return;
    }
    fail(`${applicabilityPath}.kind`, "expected fallback, typedConditions, or sourceExpression");
}

function validateItemQuantity(value, path) {
    const quantity = requireRecord(value, path);
    if (quantity.state === "known") {
        requireExactFields(quantity, ["state", "minimum", "maximum"], path);
        const minimum = requireInteger(quantity.minimum, `${path}.minimum`, 0, U32_MAX);
        const maximum = requireInteger(quantity.maximum, `${path}.maximum`, 0, U32_MAX);
        if (maximum < minimum) {
            fail(`${path}.maximum`, "must be at least the minimum");
        }
        return;
    }
    if (quantity.state === "unknown") {
        requireExactFields(quantity, ["state"], path);
        return;
    }
    if (quantity.state === "unavailable") {
        requireExactFields(quantity, ["state", "reason"], path);
        const reason = requireExactFields(
            quantity.reason,
            ["code", "detailCode", "sourceId"],
            `${path}.reason`,
        );
        requireString(reason.code, `${path}.reason.code`);
        requireNullableString(reason.detailCode, `${path}.reason.detailCode`);
        requireNullableString(reason.sourceId, `${path}.reason.sourceId`);
        return;
    }
    fail(`${path}.state`, "expected known, unknown, or unavailable");
}

function validateCatalogs(model) {
    const catalogs = Object.fromEntries(INTERNED_FIELDS.map(({ catalog }) => [
        catalog,
        requireArray(model[catalog], `$.model.${catalog}`),
    ]));
    catalogs.provenanceCatalog.forEach((value, index) => {
        validateProvenance(value, `$.model.provenanceCatalog[${index}]`);
    });
    catalogs.fishingSourceCatalog.forEach((value, index) => {
        canonicalizeCalculatorFishingSourceV1(
            value,
            `$.model.fishingSourceCatalog[${index}]`,
            (path, _code, message) => fail(path, message),
        );
    });
    catalogs.conditionCatalog.forEach((value, index) => {
        requireArray(value, `$.model.conditionCatalog[${index}]`).forEach(
            (condition, conditionIndex) => validateCondition(
                condition,
                `$.model.conditionCatalog[${index}][${conditionIndex}]`,
            ),
        );
    });
    catalogs.branchIdCatalog.forEach((value, index) => {
        if (value !== null) {
            requireString(value, `$.model.branchIdCatalog[${index}]`);
        }
    });
    catalogs.rateAlternativeCatalog.forEach((value, index) => {
        validateRateAlternative(value, `$.model.rateAlternativeCatalog[${index}]`);
    });
    return catalogs;
}

function validateOperationValues(operation, kind, path) {
    requireString(operation.operationId, `${path}.operationId`);
    if (kind !== "refuteEvidence") {
        requireString(operation.factId, `${path}.factId`);
    }
    if (Object.hasOwn(operation, "itemId")) {
        requireInteger(operation.itemId, `${path}.itemId`, 1, U32_MAX);
    }
    for (const field of ["priority", "mastery", "lifeskillLevel", "tier"]) {
        if (Object.hasOwn(operation, field)) {
            requireInteger(operation[field], `${path}.${field}`, 0, U32_MAX);
        }
    }
    for (const field of [
        "groupId",
        "optionId",
        "selectionGroupId",
        "petId",
        "targetFactId",
        "reasonCode",
    ]) {
        if (Object.hasOwn(operation, field)) {
            requireString(operation[field], `${path}.${field}`);
        }
    }
    if (Object.hasOwn(operation, "catalogOptionId")) {
        requireNullableString(operation.catalogOptionId, `${path}.catalogOptionId`);
    }
    if (Object.hasOwn(operation, "isFish") && typeof operation.isFish !== "boolean") {
        fail(`${path}.isFish`, "expected a boolean");
    }
    if (kind === "itemQuantity" || kind === "itemQuantityDefault") {
        validateItemQuantity(operation.quantity, `${path}.quantity`);
    }
}

function resolveReference(operation, reference, catalog, path) {
    const index = operation[reference];
    if (!Number.isSafeInteger(index) || index < 0) {
        fail(`${path}.${reference}`, "expected a non-negative safe integer catalog reference");
    }
    if (index >= catalog.length) {
        fail(`${path}.${reference}`, "catalog reference is out of range");
    }
    return catalog[index];
}

function validateOperationShape(operation, kind, path) {
    const shape = OPERATION_SHAPES[kind];
    const required = new Set(shape.required);
    const allowed = new Set([...shape.required, ...(shape.optional ?? [])]);
    const unknown = Object.keys(operation).find((field) => !allowed.has(field));
    if (unknown) {
        fail(`${path}.${unknown}`, "unknown field");
    }
    const missing = [...required].find((field) => !Object.prototype.hasOwnProperty.call(
        operation,
        field,
    ));
    if (missing) {
        fail(`${path}.${missing}`, "missing field");
    }
}

function expandOperation(value, path, catalogs) {
    const operation = requireRecord(value, path);
    const kind = requireString(operation.kind, `${path}.kind`);
    const references = OPERATION_REFERENCES[kind];
    if (!references) {
        fail(`${path}.kind`, `unsupported operation kind ${kind}`);
    }
    for (const { field, reference } of INTERNED_FIELDS) {
        if (Object.prototype.hasOwnProperty.call(operation, field)) {
            fail(`${path}.${field}`, `inline ${field} is forbidden; use ${reference}`);
        }
    }
    validateOperationShape(operation, kind, path);
    validateOperationValues(operation, kind, path);
    const expanded = { ...operation };
    for (const { field, reference, catalog } of INTERNED_FIELDS) {
        const requirement = field === "provenance" ? REQUIRED : references[field];
        const hasReference = Object.prototype.hasOwnProperty.call(operation, reference);
        if (!requirement && hasReference) {
            fail(`${path}.${reference}`, `is not valid for ${kind}`);
        }
        if (requirement === REQUIRED && !hasReference) {
            fail(`${path}.${reference}`, "missing required catalog reference");
        }
        if (!hasReference) {
            continue;
        }
        expanded[field] = resolveReference(operation, reference, catalogs[catalog], path);
        delete expanded[reference];
    }
    if (kind === "itemRate" || kind === "groupRate") {
        const hasAlternative = expanded.alternative !== undefined;
        if (hasAlternative && expanded.branchId === null) {
            fail(
                `${path}.branchIdRef`,
                "rate alternatives require a non-null branch ID",
            );
        }
        if (hasAlternative && (expanded.conditions?.length ?? 0) > 0) {
            fail(
                `${path}.conditionsRef`,
                "rate alternative applicability owns conditions; direct conditions must be empty",
            );
        }
    }
    return expanded;
}

function expandOperations(model, catalogs) {
    const baseFacts = requireRecord(model.baseFacts, "$.model.baseFacts");
    const facts = requireArray(baseFacts.facts, "$.model.baseFacts.facts")
        .map((operation, index) => expandOperation(
            operation,
            `$.model.baseFacts.facts[${index}]`,
            catalogs,
        ));
    const sourceLayers = requireArray(model.sourceLayers, "$.model.sourceLayers")
        .map((layer, layerIndex) => {
            const path = `$.model.sourceLayers[${layerIndex}]`;
            const value = requireRecord(layer, path);
            return {
                ...value,
                operations: requireArray(value.operations, `${path}.operations`)
                    .map((operation, operationIndex) => expandOperation(
                        operation,
                        `${path}.operations[${operationIndex}]`,
                        catalogs,
                    )),
            };
        });
    return {
        baseFacts: { ...baseFacts, facts },
        sourceLayers,
    };
}

export class CalculatorCompactModelContractError extends TypeError {
    constructor(path, message) {
        super(`calculator compact model contract failed at ${path}: ${message}`);
        this.name = "CalculatorCompactModelContractError";
        this.path = path;
    }
}

/**
 * Validates the compact transport and returns the separate expanded read model
 * used only by the inspector and projection operation index.
 */
export function expandCalculatorCompactModelBundleV1(value) {
    const model = requireExactFields(value, MODEL_FIELDS, "$.model");
    const catalogs = validateCatalogs(model);
    const { baseFacts, sourceLayers } = expandOperations(model, catalogs);
    const expanded = { ...model, baseFacts, sourceLayers };
    for (const { catalog } of INTERNED_FIELDS) {
        delete expanded[catalog];
    }
    return parseCalculatorInspectorModelV1(expanded);
}

export function parseCalculatorCompactModelBundleV1(value) {
    expandCalculatorCompactModelBundleV1(value);
    return value;
}
